
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '@/stores/notification-store';
import { cn } from '@/lib/utils';
import {
  Bell, X, CheckCircle2, AlertTriangle, AlertCircle, Info, CheckCheck,
  MessageSquare, Ticket, RefreshCw, BellRing, XCircle
} from 'lucide-react';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  ticket_new: Ticket,
  ticket_updated: RefreshCw,
  message_new: MessageSquare,
  stage_changed: RefreshCw,
};

const colorMap: Record<string, string> = {
  success: 'bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400',
  error: 'bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400',
  warning: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-600 dark:text-yellow-400',
  info: 'bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400',
  ticket_new: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-600 dark:text-indigo-400',
  ticket_updated: 'bg-purple-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400',
  message_new: 'bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400',
  stage_changed: 'bg-teal-500/10 border-teal-500/20 text-teal-600 dark:text-teal-400',
};

const dotColorMap: Record<string, string> = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  warning: 'bg-yellow-500',
  info: 'bg-blue-500',
  ticket_new: 'bg-indigo-500',
  ticket_updated: 'bg-purple-500',
  message_new: 'bg-blue-500',
  stage_changed: 'bg-teal-500',
};

function timeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'الآن';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `منذ ${minutes} دقيقة`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `منذ ${hours} ساعة`;
  const days = Math.floor(hours / 24);
  return `منذ ${days} يوم`;
}

export const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, isOpen, setOpen, removeNotification, markAsRead, markAllAsRead, clearAll, requestBrowserPermission, startPolling, stopPolling, isPolling } = useNotificationStore();

  // Start polling when component mounts (only once)
  React.useEffect(() => {
    startPolling();
    requestBrowserPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNotificationClick = (n: any) => {
    markAsRead(n.id);
    if (n.ticketId) {
      navigate(`/tickets/${n.ticketId}`);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-accent transition-colors"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5 text-primary animate-pulse" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-scale-in">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 w-80 sm:w-96 max-h-[520px] overflow-hidden bg-card border border-border rounded-xl shadow-xl z-50 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" />
                <span className="font-semibold text-sm">الإشعارات</span>
                {unreadCount > 0 && (
                  <span className="text-[10px] font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
                {isPolling && (
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" title="مراقبة الإشعارات نشطة" />
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="p-1.5 rounded-md hover:bg-accent transition-colors"
                    title="تحديد الكل كمقروء"
                  >
                    <CheckCheck className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={clearAll}
                    className="p-1.5 rounded-md hover:bg-accent transition-colors"
                    title="مسح الكل"
                  >
                    <X className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded-md hover:bg-destructive/10 transition-colors group"
                  title="إغلاق"
                >
                  <XCircle className="w-4 h-4 text-muted-foreground group-hover:text-destructive transition-colors" />
                </button>
              </div>
            </div>

            {/* List */}
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Bell className="w-8 h-8 mb-2 opacity-30" />
                  <p className="text-sm">لا توجد إشعارات</p>
                  <p className="text-[10px] mt-1 opacity-60">سيتم إشعارك بالتحديثات الجديدة</p>
                </div>
              ) : (
                <div className="divide-y divide-border/50">
                  {notifications.map((n) => {
                    const Icon = iconMap[n.type] || Info;
                    return (
                      <div
                        key={n.id}
                        className={cn(
                          'p-3 flex gap-2.5 items-start transition-colors cursor-pointer hover:bg-accent/50',
                          !n.read && 'bg-accent/30',
                          n.ticketId && 'hover:bg-primary/5'
                        )}
                        onClick={() => handleNotificationClick(n)}
                      >
                        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border', colorMap[n.type] || colorMap.info)}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={cn('text-sm leading-tight', !n.read && 'font-semibold')}>{n.title}</p>
                            <button
                              onClick={(e) => { e.stopPropagation(); removeNotification(n.id); }}
                              className="shrink-0 p-0.5 rounded hover:bg-accent"
                            >
                              <X className="w-3 h-3 text-muted-foreground" />
                            </button>
                          </div>
                          {n.message && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-muted-foreground">{timeAgo(n.timestamp)}</span>
                            {!n.read && (
                              <span className={cn('w-1.5 h-1.5 rounded-full', dotColorMap[n.type] || dotColorMap.info)} />
                            )}
                            {n.ticketId && (
                              <span className="text-[9px] text-primary font-medium">عرض التذكرة ←</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {isPolling && (
              <div className="p-2 border-t border-border text-center">
                <span className="text-[9px] text-muted-foreground/60 flex items-center justify-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-green-500" />
                  مراقبة الإشعارات نشطة
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
